import { useRef, useState } from "react";
import { Input } from "reactstrap";

import useUserContext from "Hooks/useUserContext";
import useAlertContext from "Hooks/useAlertContext";

/** Used in NavMenu.tsx. It simply extracts the upload logo logic from a too-large component. */
export default function useUploadLogo() {
    /** Set to a timestamp on each upload to cache-bust the logo URL */
    const [isLogoChanged, setIsLogoChanged] = useState(0);

    const [userContext] = useUserContext();
    const { newAlert } = useAlertContext();

    /** Used for logo upload. Ref allows access to the input to programatically
     * fire a click event on it */
    const fileInputRef = useRef<HTMLInputElement>(null);

    /** Which variant is being uploaded — "" for light, "dark" for dark */
    const pendingVariant = useRef<string>("");

    /** Fires when upload logo is clicked. Opens the window to pick a file to upload. */
    function selectLogoToUpload(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        pendingVariant.current = "";
        if (!fileInputRef.current) {
            return;
        }
        fileInputRef.current?.click();
    }

    function selectDarkLogoToUpload(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        pendingVariant.current = "dark";
        if (!fileInputRef.current) {
            return;
        }
        fileInputRef.current?.click();
    }

    async function uploadLogo(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (file) {
            const formData = new FormData();
            formData.append("file", file);

            if (fileInputRef.current) fileInputRef.current.value = "";

            const variant = pendingVariant.current;
            const queryString = variant ? `?variant=${variant}` : "";

            const response = await fetch(
                `/api/files/logos/${userContext.companyId}${queryString}`,
                {
                    method: "POST",
                    body: formData,
                }
            );

            let data: Response | string;
            try {
                data = await response.json();
            } catch {
                data = response;
            }
            console.log(data);

            const label = variant ? "Dark logo" : "Logo";
            if (response.ok) {
                newAlert({
                    message: `${label} uploaded successfully`,
                    color: "success",
                });
                setIsLogoChanged(Date.now());
            } else {
                newAlert({
                    message: `${label} upload failed`,
                });
            }
        }
    }

    const hiddenUploadLogoFileInput = (
        <Input
            innerRef={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/svg+xml"
            hidden
            onChange={uploadLogo}
        />
    );

    return {
        isLogoChanged,
        selectLogoToUpload,
        selectDarkLogoToUpload,
        hiddenUploadLogoFileInput,
    };
}
